import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import * as PIXI from 'pixi.js';
import { localToTile } from '../../../domain/tileMath';
import { drawAdminDragPreview } from '../render/overlays';
import type { RoomPixiRoots } from '../useRoomScene';
import type { RoomSceneState } from '../state/roomSceneState';
import { asFurnitureSprite } from '../render/pixiExtensions';

// ── Viewport drag-select / admin tile-editor pointer effect ──
// Extracted verbatim from RoomCanvas (decomposition step: viewport drag-select
// effect). The effect body is byte-identical to the original except that:
//  * RoomCanvas-owned refs, state setters and callbacks are received as parameters;
//  * the module-level scene state (roomViewport, roomContainer) is read through
//    pixiRootsRef — same live RoomPixiRoots accessor as useRoomScene (ref form so
//    react-hooks/immutability allows PIXI eventMode / plugin writes);
//  * removeGreenOutline is a hoisted function declaration in RoomCanvas, so it is
//    passed directly (no TDZ arrow-wrapper needed).
// Constants used ONLY by this effect moved here with it.

const ADMIN_DOUBLE_CLICK_THRESHOLD_MS = 500; // Для адмін режиму (як в MapCanvas)
const ADMIN_DOUBLE_CLICK_DISTANCE_THRESHOLD = 20; // Максимальна відстань між кліками в пікселях

export interface UseViewportDragSelectParams {
  isSceneReady: boolean;
  isAdmin: boolean;
  pixiRootsRef: MutableRefObject<RoomPixiRoots>;
  sceneRef: MutableRefObject<RoomSceneState>;
  removeGreenOutline: () => void;
  setAdminSelectedTiles: Dispatch<SetStateAction<Set<string>>>;
}

export function useViewportDragSelect({
  isSceneReady,
  isAdmin,
  pixiRootsRef,
  sceneRef,
  removeGreenOutline,
  setAdminSelectedTiles,
}: UseViewportDragSelectParams): void {
  // Admin double-click handler for room coordinates (similar to MapCanvas)
  useEffect(() => {
    const roots = pixiRootsRef.current;
    if (!isSceneReady || !roots.roomViewport || !isAdmin) return;

    const viewport = roots.roomViewport;

    /** Convert a global PIXI pointer position to isometric tile coords. Math extracted to room/tileMath.ts. */
    const pointerToTile = (globalPos: { x: number; y: number }): { tileX: number; tileY: number } | null => {
      if (!roots.roomContainer) return null;
      return localToTile(roots.roomContainer.toLocal(globalPos));
    };

    /** Draw the live drag-preview rectangle on adminDragPreviewOverlayRef. Thin wrapper — drawing logic extracted to room/render/overlays.ts. */
    const drawDragPreview = (startTile: { tileX: number; tileY: number }, endTile: { tileX: number; tileY: number }) => {
      drawAdminDragPreview(
        sceneRef.current.overlays.adminDragPreview,
        sceneRef.current.admin.tileMode,
        sceneRef.current.admin.dragMode === 'add',
        sceneRef.current.admin.tileCenter ?? { tileX: 0, tileY: 0 },
        sceneRef.current.admin.selectedTiles,
        startTile,
        endTile,
      );
    };

    // Flag: true while a 2nd-finger (non-primary) touch is active in tile-editor mode.
    // Used to re-pause viewport drag after multi-touch camera gesture ends.
    let adminMultiTouchActive = false;

    const handleViewportPointerDown = (event: PIXI.FederatedPointerEvent) => {
      // TOP-LEVEL DIAGNOSTIC LOG — fires before any early returns
      // console.log('[VP.down] called', { pointerId: event.pointerId, isPrimary: event.isPrimary, type: event.pointerType, mode: sceneRef.current.admin.tileMode, hasVP: !!roots.roomViewport, hasRC: !!roots.roomContainer });
      if (!roots.roomViewport || !roots.roomContainer) {
        // console.warn('[VP.down] early exit: roots.roomViewport or roots.roomContainer is null');
        return;
      }

      // Empty-space tap: clear furniture selection outline.
      // Furniture sprite pointerdowns bubble here too — furnitureJustTappedRef prevents false clears.
      if (sceneRef.current.drag.furnitureJustTapped) {
        sceneRef.current.drag.furnitureJustTapped = false;
      } else {
        removeGreenOutline();
      }

      // Tile selection mode: start a drag-paint gesture
      if (sceneRef.current.admin.tileMode !== null) {
        // console.log('[TileEditor] pointerdown', { isPrimary: event.isPrimary, pointerId: event.pointerId, pointerType: event.pointerType, buttons: event.buttons, mode: sceneRef.current.admin.tileMode });
        if (!event.isPrimary) {
          // Second+ finger: let viewport handle camera pan/zoom, cancel tile drag
          adminMultiTouchActive = true;
          roots.roomViewport.plugins.resume('drag');
          sceneRef.current.admin.dragStartTile = null;
          const preview = sceneRef.current.overlays.adminDragPreview;
          if (preview) { preview.clear(); preview.visible = false; }
          // console.log('[TileEditor] multi-touch detected, resuming drag');
          return;
        }
        // First (primary) finger/mouse: paint tiles
        adminMultiTouchActive = false;
        const tile = pointerToTile(event.global);
        // console.log('[TileEditor] tile from pointerToTile:', tile, 'center:', sceneRef.current.admin.tileCenter);
        if (!tile) { /* console.warn('[TileEditor] no tile from pointerToTile, returning'); */ return; }
        const center = sceneRef.current.admin.tileCenter ?? { tileX: 0, tileY: 0 };
        const key = `${tile.tileX - center.tileX},${tile.tileY - center.tileY}`;
        // Decide add vs remove based on whether the start tile is already selected
        sceneRef.current.admin.dragMode = sceneRef.current.admin.selectedTiles.has(key) ? 'remove' : 'add';
        sceneRef.current.admin.dragStartTile = tile;
        drawDragPreview(tile, tile);
        // console.log('[TileEditor] drag started at', tile, 'mode:', sceneRef.current.admin.dragMode);
        return; // Don't process as admin double-click
      }

      // Skip if clicking on furniture (let furniture handlers process it)
      const clickedFurniture = asFurnitureSprite(event.target as PIXI.Container | undefined)?.furnitureData;
      if (clickedFurniture) return;

      const currentTime = Date.now();
      const currentX = event.client.x;
      const currentY = event.client.y;

      // Перевіряємо, чи це подвійний клік
      if (sceneRef.current.admin.clickStart) {
        const timeDiff = currentTime - sceneRef.current.admin.clickStart.timestamp;
        const distance = Math.sqrt(
          Math.pow(currentX - sceneRef.current.admin.clickStart.x, 2) +
          Math.pow(currentY - sceneRef.current.admin.clickStart.y, 2)
        );

        // Якщо час між кліками менший за поріг і відстань менша за поріг - це подвійний клік
        if (timeDiff < ADMIN_DOUBLE_CLICK_THRESHOLD_MS && distance < ADMIN_DOUBLE_CLICK_DISTANCE_THRESHOLD) {
          // Скидаємо посилання після обробки подвійного кліку
          sceneRef.current.admin.clickStart = null;
          return;
        }
      }

      // Зберігаємо дані про поточний клік для можливого подвійного кліку
      sceneRef.current.admin.clickStart = {
        x: currentX,
        y: currentY,
        roomX: roots.roomContainer.toLocal(event.global).x,
        roomY: roots.roomContainer.toLocal(event.global).y,
        timestamp: currentTime
      };

      // Встановлюємо таймер для скидання посилання, якщо не буде другого кліку
      setTimeout(() => {
        if (sceneRef.current.admin.clickStart && sceneRef.current.admin.clickStart.timestamp === currentTime) {
          sceneRef.current.admin.clickStart = null;
        }
      }, ADMIN_DOUBLE_CLICK_THRESHOLD_MS);
    };

    const handleViewportPointerMove = (event: PIXI.FederatedPointerEvent) => {
      if (sceneRef.current.admin.tileMode === null) return;
      if (!sceneRef.current.admin.dragStartTile) {
        // Log occasionally to avoid spam
        // if (Math.random() < 0.02) console.log('[TileEditor] pointermove: no dragStart, skipping');
        return;
      }
      if (!(event.buttons & 1)) {
        // console.log('[TileEditor] pointermove: buttons=', event.buttons, 'skipping (no primary button)');
        return;
      }
      const tile = pointerToTile(event.global);
      if (!tile) return;
      drawDragPreview(sceneRef.current.admin.dragStartTile, tile);
    };

    const commitDragSelection = (event: PIXI.FederatedPointerEvent) => {
      // console.log('[TileEditor] pointerup/outside', { isPrimary: event.isPrimary, pointerId: event.pointerId, type: event.type, hasDragStart: !!sceneRef.current.admin.dragStartTile, mode: sceneRef.current.admin.tileMode });
      if (!event.isPrimary) {
        // Non-primary pointer released: end multi-touch camera mode, re-pause drag
        if (adminMultiTouchActive && sceneRef.current.admin.tileMode !== null) {
          adminMultiTouchActive = false;
          // Defer pause so it doesn't interrupt current event processing
          requestAnimationFrame(() => roots.roomViewport?.plugins.pause('drag'));
        }
        return;
      }
      if (sceneRef.current.admin.tileMode === null || !sceneRef.current.admin.dragStartTile) return;
      const endTile = pointerToTile(event.global) ?? sceneRef.current.admin.dragStartTile;
      const start = sceneRef.current.admin.dragStartTile;
      const center = sceneRef.current.admin.tileCenter ?? { tileX: 0, tileY: 0 };
      const isAdd = sceneRef.current.admin.dragMode === 'add';

      const minTX = Math.min(start.tileX, endTile.tileX);
      const maxTX = Math.max(start.tileX, endTile.tileX);
      const minTY = Math.min(start.tileY, endTile.tileY);
      const maxTY = Math.max(start.tileY, endTile.tileY);

      setAdminSelectedTiles((prev) => {
        const next = new Set(prev);
        for (let tx = minTX; tx <= maxTX; tx++) {
          for (let ty = minTY; ty <= maxTY; ty++) {
            const dx = tx - center.tileX;
            const dy = ty - center.tileY;
            const key = `${dx},${dy}`;
            if (isAdd) next.add(key); else next.delete(key);
          }
        }
        return next;
      });

      // Clear drag state
      sceneRef.current.admin.dragStartTile = null;
      const preview = sceneRef.current.overlays.adminDragPreview;
      if (preview) { preview.clear(); preview.visible = false; }
    };

    viewport.eventMode = 'static';
    viewport.on('pointerdown', handleViewportPointerDown);
    viewport.on('pointermove', handleViewportPointerMove);
    viewport.on('pointerup', commitDragSelection);
    viewport.on('pointerupoutside', commitDragSelection);

    return () => {
      // console.log('[VP] useEffect cleanup: removing pointer listeners (isSceneReady/isAdmin changed)');
      viewport.off('pointerdown', handleViewportPointerDown);
      viewport.off('pointermove', handleViewportPointerMove);
      viewport.off('pointerup', commitDragSelection);
      viewport.off('pointerupoutside', commitDragSelection);
      // .current у cleanup: live admin drag state на detach, не mount snapshot.
      /* eslint-disable react-hooks/exhaustive-deps -- cleanup needs live admin bag, not mount snapshot */
      sceneRef.current.admin.clickStart = null;
      sceneRef.current.admin.dragStartTile = null;
      /* eslint-enable react-hooks/exhaustive-deps */
      adminMultiTouchActive = false;
    };
    // Тригер: isSceneReady + isAdmin. removeGreenOutline — часто нова fn щорендеру;
    // pixiRootsRef — module-stable identity; live getters on .current.
    // setAdminSelectedTiles — setState stable. Re-bind listeners лише при admin gate change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- admin/scene ready only; omit unstable outline fn
  }, [isSceneReady, isAdmin, pixiRootsRef, sceneRef, setAdminSelectedTiles]);
}
